/**
 * DocumentsPanel — sidebar tab listing all locally-stored documents as a
 * folder tree derived from their path-like names (`work/client-a/logo`).
 *
 * Supports switching, creating (at root or inside a folder), renaming /
 * moving (edit the full path inline), duplicating and deleting documents.
 */

import { useExcalidrawAPI } from "@excalidraw/excalidraw";
import { openConfirmModal } from "@excalidraw/excalidraw/components/OverwriteConfirm/OverwriteConfirmState";
import {
  chevronDownIcon,
  chevronRight,
  DuplicateIcon,
  pencilIcon,
  PlusIcon,
  TrashIcon,
} from "@excalidraw/excalidraw/components/icons";
import clsx from "clsx";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";

import { atom, useAtom, useAtomValue } from "../app-jotai";
import { AuthPanel } from "../auth/AuthPanel";
import { isCollaboratingAtom } from "../collab/Collab";

import {
  activeDocumentIdAtom,
  createDocument,
  deleteDocument,
  documentsAtom,
  duplicateDocument,
  getDocumentBasename,
  normalizeDocumentName,
  renameDocument,
  switchToDocument,
} from "./DocumentManager";

import "./DocumentsPanel.scss";

import type { DocumentMeta } from "./documentStorage";

/** which folders are expanded; null = derive from the active document */
const expandedFoldersAtom = atom<Record<string, boolean> | null>(null);

// ---------------------------------------------------------------------------
// tree building
// ---------------------------------------------------------------------------

type TreeFolder = {
  kind: "folder";
  name: string;
  path: string;
  children: TreeNode[];
};
type TreeDocument = { kind: "document"; meta: DocumentMeta };
type TreeNode = TreeFolder | TreeDocument;

const nodeName = (node: TreeNode) =>
  node.kind === "folder" ? node.name : getDocumentBasename(node.meta.name);

const sortTreeChildren = (folder: TreeFolder) => {
  folder.children.sort((a, b) => {
    if (a.kind !== b.kind) {
      return a.kind === "folder" ? -1 : 1;
    }
    return nodeName(a).localeCompare(nodeName(b));
  });
  folder.children.forEach((child) => {
    if (child.kind === "folder") {
      sortTreeChildren(child);
    }
  });
};

const buildTree = (documents: DocumentMeta[]): TreeNode[] => {
  const root: TreeFolder = { kind: "folder", name: "", path: "", children: [] };

  for (const meta of documents) {
    const segments = meta.name.split("/");
    let parent = root;
    for (let i = 0; i < segments.length - 1; i++) {
      const path = segments.slice(0, i + 1).join("/");
      let folder = parent.children.find(
        (child): child is TreeFolder =>
          child.kind === "folder" && child.path === path,
      );
      if (!folder) {
        folder = {
          kind: "folder",
          name: segments[i],
          path,
          children: [],
        };
        parent.children.push(folder);
      }
      parent = folder;
    }
    parent.children.push({ kind: "document", meta });
  }

  sortTreeChildren(root);
  return root.children;
};

// ---------------------------------------------------------------------------
// icons
// ---------------------------------------------------------------------------

export const documentsSidebarTabIcon = (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M15 3v4a1 1 0 0 0 1 1h4" />
    <path d="M18 17h-7a2 2 0 0 1 -2 -2v-10a2 2 0 0 1 2 -2h4l5 5v7a2 2 0 0 1 -2 2z" />
    <path d="M16 17v2a2 2 0 0 1 -2 2h-7a2 2 0 0 1 -2 -2v-9a2 2 0 0 1 2 -2h2" />
  </svg>
);

const folderIcon = (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M5 4h4l3 3h7a2 2 0 0 1 2 2v8a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2v-11a2 2 0 0 1 2 -2" />
  </svg>
);

const documentIcon = (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M14 3v4a1 1 0 0 0 1 1h4" />
    <path d="M17 21h-10a2 2 0 0 1 -2 -2v-14a2 2 0 0 1 2 -2h7l5 5v9a2 2 0 0 1 -2 2z" />
  </svg>
);

// ---------------------------------------------------------------------------
// rename input
// ---------------------------------------------------------------------------

const RenameInput = ({
  meta,
  onCommit,
  onCancel,
}: {
  meta: DocumentMeta;
  onCommit: (value: string) => void;
  onCancel: () => void;
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const committedRef = useRef(false);

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  const commit = (value: string) => {
    if (committedRef.current) {
      return;
    }
    committedRef.current = true;
    onCommit(value);
  };

  return (
    <input
      ref={inputRef}
      className="documents-panel__rename-input"
      defaultValue={meta.name}
      title='Use "/" to organize into folders, e.g. "work/project/sketch"'
      onBlur={(event) => commit(event.target.value)}
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => {
        // keep global canvas shortcuts from firing while typing
        event.stopPropagation();
        if (event.key === "Enter") {
          commit((event.target as HTMLInputElement).value);
        } else if (event.key === "Escape") {
          committedRef.current = true;
          onCancel();
        }
      }}
    />
  );
};

// ---------------------------------------------------------------------------
// panel
// ---------------------------------------------------------------------------

export const DocumentsPanel = () => {
  const excalidrawAPI = useExcalidrawAPI();
  const [documents] = useAtom(documentsAtom);
  const [activeDocumentId] = useAtom(activeDocumentIdAtom);
  const isCollaborating = useAtomValue(isCollaboratingAtom);
  const [expandedFolders, setExpandedFolders] = useAtom(expandedFoldersAtom);
  const [renamingId, setRenamingId] = useState<string | null>(null);

  const tree = useMemo(() => buildTree(documents), [documents]);

  // folders the active document lives in, expanded by default
  const activeAncestors = useMemo(() => {
    const ancestors: Record<string, boolean> = {};
    const active = documents.find((meta) => meta.id === activeDocumentId);
    if (active) {
      const segments = active.name.split("/");
      for (let i = 1; i < segments.length; i++) {
        ancestors[segments.slice(0, i).join("/")] = true;
      }
    }
    return ancestors;
  }, [documents, activeDocumentId]);

  const isExpanded = (path: string) =>
    expandedFolders ? !!expandedFolders[path] : !!activeAncestors[path];

  const toggleFolder = (path: string) => {
    const next = { ...activeAncestors, ...expandedFolders };
    next[path] = !isExpanded(path);
    setExpandedFolders(next);
  };

  const expandPath = (path: string) => {
    if (!isExpanded(path)) {
      const next = { ...activeAncestors, ...expandedFolders };
      next[path] = true;
      setExpandedFolders(next);
    }
  };

  const handleCreate = async (parentPath: string | null) => {
    if (!excalidrawAPI || isCollaborating) {
      return;
    }
    const meta = await createDocument(parentPath, {
      switchTo: true,
      excalidrawAPI,
    });
    if (parentPath) {
      expandPath(parentPath);
    }
    setRenamingId(meta.id);
  };

  const handleSwitch = (meta: DocumentMeta) => {
    if (!excalidrawAPI || isCollaborating || meta.id === activeDocumentId) {
      return;
    }
    switchToDocument(meta.id, excalidrawAPI);
  };

  const handleRenameCommit = async (meta: DocumentMeta, value: string) => {
    setRenamingId(null);
    if (normalizeDocumentName(value) !== meta.name) {
      await renameDocument(meta.id, value, excalidrawAPI ?? undefined);
    }
  };

  const handleDuplicate = (meta: DocumentMeta) => {
    if (!excalidrawAPI || isCollaborating) {
      return;
    }
    duplicateDocument(meta.id, excalidrawAPI);
  };

  const handleDelete = async (meta: DocumentMeta) => {
    if (!excalidrawAPI || isCollaborating) {
      return;
    }
    const confirmed = await openConfirmModal({
      title: `Delete “${meta.name}”?`,
      description:
        "This permanently deletes the drawing from this browser. This action cannot be undone.",
      actionLabel: "Delete",
      color: "danger",
    });
    if (confirmed) {
      await deleteDocument(meta.id, excalidrawAPI);
    }
  };

  // -------------------------------------------------------------------------

  const renderDocumentRow = (meta: DocumentMeta, depth: number) => {
    const isActive = meta.id === activeDocumentId;
    return (
      <div
        key={meta.id}
        className={clsx("documents-panel__row", {
          "documents-panel__row--active": isActive,
        })}
        style={{ paddingLeft: `${depth * 1 + 0.25}rem` }}
      >
        {renamingId === meta.id ? (
          <RenameInput
            meta={meta}
            onCommit={(value) => handleRenameCommit(meta, value)}
            onCancel={() => setRenamingId(null)}
          />
        ) : (
          <>
            <button
              type="button"
              className="documents-panel__row-main"
              title={meta.name}
              onClick={() => handleSwitch(meta)}
            >
              <span className="documents-panel__icon">{documentIcon}</span>
              <span className="documents-panel__name">
                {getDocumentBasename(meta.name)}
              </span>
              {meta.syncState && meta.syncState !== "local" && (
                <span
                  className={`documents-panel__sync documents-panel__sync--${meta.syncState}`}
                  title={
                    meta.syncState === "conflict"
                      ? "This drawing changed on another device"
                      : `Cloud sync: ${meta.syncState}`
                  }
                >
                  {meta.syncState === "saving"
                    ? "Saving"
                    : meta.syncState === "offline"
                    ? "Offline"
                    : meta.syncState === "conflict"
                    ? "Conflict"
                    : "Saved"}
                </span>
              )}
            </button>
            <span className="documents-panel__actions">
              <button
                type="button"
                title="Rename / move"
                onClick={(event) => {
                  event.stopPropagation();
                  setRenamingId(meta.id);
                }}
              >
                {pencilIcon}
              </button>
              <button
                type="button"
                title="Duplicate"
                onClick={(event) => {
                  event.stopPropagation();
                  handleDuplicate(meta);
                }}
              >
                {DuplicateIcon}
              </button>
              <button
                type="button"
                title="Delete"
                onClick={(event) => {
                  event.stopPropagation();
                  handleDelete(meta);
                }}
              >
                {TrashIcon}
              </button>
            </span>
          </>
        )}
      </div>
    );
  };

  const renderFolderRow = (folder: TreeFolder, depth: number) => {
    const expanded = isExpanded(folder.path);
    return (
      <div
        key={`folder:${folder.path}`}
        className="documents-panel__row documents-panel__row--folder"
        style={{ paddingLeft: `${depth * 1 + 0.25}rem` }}
      >
        <button
          type="button"
          className="documents-panel__row-main"
          onClick={() => toggleFolder(folder.path)}
        >
          <span className="documents-panel__chevron">
            {expanded ? chevronDownIcon : chevronRight}
          </span>
          <span className="documents-panel__icon">{folderIcon}</span>
          <span className="documents-panel__name">{folder.name}</span>
        </button>
        <span className="documents-panel__actions">
          <button
            type="button"
            title="New document here"
            onClick={(event) => {
              event.stopPropagation();
              handleCreate(folder.path);
            }}
          >
            {PlusIcon}
          </button>
        </span>
      </div>
    );
  };

  const renderNodes = (nodes: TreeNode[], depth: number): React.ReactNode =>
    nodes.map((node) =>
      node.kind === "folder" ? (
        <Fragment key={`folder:${node.path}`}>
          {renderFolderRow(node, depth)}
          {isExpanded(node.path) && renderNodes(node.children, depth + 1)}
        </Fragment>
      ) : (
        renderDocumentRow(node.meta, depth)
      ),
    );

  return (
    <div className="documents-panel">
      <div className="documents-panel__header">
        <span className="documents-panel__title">Documents</span>
        <button
          type="button"
          className="documents-panel__new-button"
          title="New document"
          disabled={!excalidrawAPI || isCollaborating}
          onClick={() => handleCreate(null)}
        >
          {PlusIcon}
          <span>New</span>
        </button>
      </div>

      <AuthPanel />

      {isCollaborating && (
        <div className="documents-panel__notice">
          Document switching is disabled while collaborating.
        </div>
      )}

      <div className="documents-panel__tree">
        {tree.length > 0 ? (
          renderNodes(tree, 0)
        ) : (
          <div className="documents-panel__empty">No documents yet.</div>
        )}
      </div>
    </div>
  );
};
